"use client";

/**
 * Slack ops alerts deep-link to `/task/<id>?as=<ownerUid>&w=<tenantId>`
 * (see `ops_alerts._task_link`, ENG-2951). For staff, this hook auto-starts
 * Staff Impersonation as the task owner and pins the task's workspace, so
 * TasksPanel can load the task under the right identity.
 *
 * **The hop params stay on the URL once the hop succeeds** (ENG-5147). They
 * used to be stripped, which left a bare `/task/<id>` in the address bar — and
 * since the impersonation token is in-memory only by design (ENG-4155), that
 * URL could not re-establish staff context. A reload, a restored tab, or the
 * same URL in a second tab then resolved as the REAL staff user against their
 * own workspace, so the task — private to its creator + workspace — 404'd, and
 * the operator got "Can't find that" over a thread that was perfectly fine.
 * Keeping `?as=`/`?w=` makes the URL the durable carrier of the hop: every
 * fresh load re-mints through `POST /api/staff/impersonate`, which is
 * re-authorized server-side each time, so nothing about the ENG-4155 invariant
 * changes — the token itself is still never persisted anywhere.
 *
 * Scope is the URL the operator ARRIVED on. In-app navigation builds plain
 * paths (`taskPath(id)`), so clicking through to a second task drops the params
 * and a reload there 404s as before; keeping every impersonated URL stamped
 * needs the URL kept in sync with a workspace the switcher can change in place
 * (`setActiveWorkspace` deliberately does not reload while impersonating), and
 * that is ENG-5151, not this.
 *
 * Two consequences are deliberate. `AuthGuard` keeps skipping the onboarding
 * gate while `?as=` is present, which is what we want for the whole life of an
 * impersonated view rather than just its first moment; and `staffHopPending`
 * below — not the bare presence of `?as=` — is what tells TasksPanel to hold
 * its skeleton, so a kept param can't strand the panel waiting for a hop that
 * already happened.
 *
 * The params are still stripped in exactly two cases, both of which mean "no
 * hop is coming": a non-staff visitor (no privilege escalation, and no pinning
 * into a workspace this viewer may not even belong to — ENG-4830), and a hop
 * that failed (so TasksPanel stops waiting and surfaces a real error instead of
 * an infinite skeleton).
 *
 * The `?w=`-only workspace hop (shared-project emails, no `as`) is a different
 * case entirely and never reaches this hook — it's owned by AppShell's own
 * one-shot `?w=` effect.
 */

import { useEffect, useRef, useSyncExternalStore } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import {
  getActiveWorkspaceTenantId,
  notifyTenantContextChanged,
  setActiveWorkspacePin,
} from "@/lib/api";
import { logger } from "@/lib/log";
import {
  markStaffHopSettled,
  staffHopSettled,
  staffHopSettledOnServer,
  subscribeStaffHop,
} from "@/lib/staff-hop-state";
import { unloadIdentityScopedCache } from "@/lib/queries";

const log = logger("staff-task-deep-link");

/** Query key for the impersonation target (task owner uid). */
export const STAFF_AS_PARAM = "as";

/**
 * Identity of one hop, as both the URL and the hook spell it. One definition so
 * the gate below and `@/lib/staff-hop-state` can never disagree about what
 * "the same hop" means.
 */
export function staffHopKey(asParam: string | null, wParam: string | null): string {
  return `${asParam?.trim() ?? ""}|${wParam?.trim() ?? ""}`;
}

/**
 * True while a staff hop named on the URL still has to happen — i.e. callers
 * must not fetch tenant-scoped data yet, because it would go out under the
 * wrong identity or the wrong workspace.
 *
 * Every input is a value available **during render**, deliberately (codex P2 ×2
 * on #2649 — `@/lib/staff-hop-state` carries the full history). The gate this
 * replaced read `?as=`'s presence, which the hop then stripped; ENG-5147 keeps
 * the params, so the test is instead "does the URL name a hop the hook has
 * already applied". Three ways it can fail to:
 *
 *  - not impersonating the named target yet (a target hop);
 *  - impersonating them, but in a different workspace than the URL asks for —
 *    the case a target comparison alone cannot see;
 *  - either of those, before the hook's effect has run at all. React runs
 *    passive effects child-first, so the consumer's loader effect fires before
 *    the hook's in the same commit; a signal the hook publishes from its own
 *    effect would always be one commit too late.
 *
 * `settledHopKey` is what keeps the second case from hanging: it is published on
 * every ending, so a workspace the target turns out not to belong to (where the
 * pin never does match the URL) releases the gate instead of skeletoning
 * forever.
 *
 * A blank `?as=` is not a hop (the effect below ignores it and never strips it),
 * so it must not gate anything either — same reasoning as
 * `AuthGuard.staffDeepLinkPending`. Non-staff is never pending: the hook strips
 * the params and does nothing, and the viewer's own data is what should load.
 */
export function staffHopPending(input: {
  asParam: string | null;
  wParam: string | null;
  isStaff: boolean;
  impersonatedTargetUid: string | null;
  settledHopKey: string | null;
}): boolean {
  if (!input.isStaff) return false;
  const target = input.asParam?.trim();
  if (!target) return false;
  if (input.impersonatedTargetUid !== target) return true;
  return input.settledHopKey !== staffHopKey(target, input.wParam);
}

/**
 * `staffHopPending` wired to the live settled-hop signal — what a component
 * should call. Keeps the store subscription in one place instead of asking every
 * consumer to remember the three `useSyncExternalStore` arguments, and keeps the
 * predicate above pure enough to unit-test without React.
 */
export function useStaffHopPending(
  asParam: string | null,
  wParam: string | null,
  isStaff: boolean,
  impersonatedTargetUid: string | null,
): boolean {
  const settledHopKey = useSyncExternalStore(
    subscribeStaffHop,
    staffHopSettled,
    staffHopSettledOnServer,
  );
  return staffHopPending({
    asParam,
    wParam,
    isStaff,
    impersonatedTargetUid,
    settledHopKey,
  });
}

export function useStaffTaskDeepLink(): void {
  const {
    loading,
    isStaff,
    impersonation,
    beginImpersonation,
    clearImpersonation,
  } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const asParam = searchParams.get(STAFF_AS_PARAM);
  const wParam = searchParams.get("w");
  // One in-flight hop at a time; ignore duplicate effect fires (StrictMode /
  // searchParams identity churn) for the same target. Keyed by as+w so a
  // same-target workspace hop doesn't collide with a prior as-only hop.
  const inflightRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!asParam) return;

    const hopKey = staffHopKey(asParam, wParam);

    const stripHopParams = () => {
      const next = new URLSearchParams(searchParams.toString());
      next.delete(STAFF_AS_PARAM);
      next.delete("w");
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    };

    // Non-staff: this link is not for this viewer at all, so strip both hop
    // params and do nothing else. `as` is minted in exactly one place —
    // `ops_alerts._task_link` — so a URL carrying it is by construction a
    // staff ops-alert hop; its `w` names the alert TARGET's workspace, which
    // this non-staff viewer need not (and per ENG-4830, often does not)
    // belong to. Applying that pin anyway durably locked a non-member into
    // another tenant's workspace (setActiveWorkspacePin has no impersonation
    // session to scope it to here, so it lands in plain localStorage), and
    // every request then failed closed with 403 WORKSPACE_ACCESS_DENIED until
    // the recovery path in lib/api.ts dropped the pin and reloaded — two
    // wasted full reloads and a 403 storm to arrive back at doing nothing.
    //
    // This deliberately reverses the `w`-application an earlier codex P2
    // added to this branch. That concern doesn't apply here: a `w`-only link
    // (no `as` — the shared-project email case) never reaches this branch,
    // because the effect returns above when `as` is absent, and AppShell owns
    // that case with its own one-shot `?w=` effect. `w` only matters in this
    // hook when `as` is also present, i.e. only for the staff ops-alert hop,
    // where the target's workspace is never this viewer's to apply.
    if (!isStaff) {
      stripHopParams();
      return;
    }

    // Already impersonating the right user — nothing to do but make sure the
    // workspace matches. The params stay (ENG-5147): this branch is exactly
    // the state a reload lands in once the re-mint completes, so stripping
    // here would throw away the URL that got us here. When the link also hops
    // `?w=` to a different tenant, pin Active-Workspace and refresh
    // `/api/me` so AuthGuard's onboarding gate (and chrome) evaluate against
    // the new tenant, not the previous one (codex P2 on #2045).
    if (impersonation?.targetUid === asParam) {
      const needsWorkspaceHop =
        Boolean(wParam) && wParam !== getActiveWorkspaceTenantId();
      if (!needsWorkspaceHop) {
        if (wParam && typeof window !== "undefined") {
          setActiveWorkspacePin(wParam);
        }
        // Settle even though there was nothing to do: this URL's hop IS applied,
        // and the gate is now keyed on that. Returning silently would leave a
        // key from an earlier hop published and hold the consumer's skeleton up
        // forever (e.g. `A|X` settled, then a plain `?as=A` URL keyed `A|`).
        markStaffHopSettled(hopKey);
        return;
      }
      if (inflightRef.current === hopKey) return;
      inflightRef.current = hopKey;
      void (async () => {
        try {
          setActiveWorkspacePin(wParam!);
          // Through the coordinator, not refreshMe() directly: an older tenant
          // resolution may already have `/api/me` in flight, and an
          // uncoordinated refresh here can settle before it and then be
          // overwritten by that staler response (codex P1). The coordinator
          // serialises them by pin version.
          await notifyTenantContextChanged();
          // SWR keys (tasks, projects, workspace-settings, …) aren't tenant-
          // scoped — they rely on the X-Active-Workspace header set above.
          // A plain per-key invalidate leaves a response already in flight
          // under the PREVIOUS tenant free to land after and repopulate the
          // same key (codex P2 on #2045 was one symptom of this).
          unloadIdentityScopedCache();
        } catch (e) {
          log.warn("staff same-target workspace hop failed", {
            as: asParam,
            w: wParam,
            error: e,
          });
        } finally {
          markStaffHopSettled(hopKey);
          if (inflightRef.current === hopKey) inflightRef.current = null;
        }
      })();
      return;
    }

    if (inflightRef.current === hopKey) return;
    inflightRef.current = hopKey;
    void (async () => {
      try {
        // Switching targets mid-session: soft-clear first so we don't reload
        // onto /staff and lose the deep-link destination. clearImpersonation
        // also drops the prior Active-Workspace pin so the next mint doesn't
        // send a customer workspace the real staff user isn't in.
        if (impersonation && impersonation.targetUid !== asParam) {
          await clearImpersonation();
        }
        await beginImpersonation(asParam, {
          workspaceTenantId: wParam,
        });
        // Drop every SWR cache entry so the sidebar/lists reflect the
        // impersonated user's tenant (stopImpersonation reloads the page for
        // the same reason; we can't reload here without losing the token).
        unloadIdentityScopedCache();
        // Params deliberately kept (ENG-5147) — they are what lets the next
        // load of this URL re-mint instead of 404ing as the staff user.
        // `staffHopPending` reads the impersonation target, not the param, so
        // TasksPanel is released by the hop itself rather than by the strip.
      } catch (e) {
        log.warn("staff task deep-link impersonation failed", {
          as: asParam,
          w: wParam,
          error: e,
        });
        // A failed hop is not coming back on its own, so here the strip still
        // earns its place: it releases TasksPanel, and the ensuing getTask 404
        // surfaces a real error overlay rather than an infinite skeleton.
        stripHopParams();
      } finally {
        markStaffHopSettled(hopKey);
        if (inflightRef.current === hopKey) inflightRef.current = null;
      }
    })();
  }, [
    loading,
    isStaff,
    asParam,
    wParam,
    impersonation,
    beginImpersonation,
    clearImpersonation,
    router,
    pathname,
    searchParams,
  ]);
}
